import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { EMPTY, Subject, catchError, finalize, switchMap, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';
import { MonthData } from './models/sla.models';
import { pctMet, slaBand, SlaBand } from './sla-rules';

const LAST_TWO_WEEKS = 'last-2-weeks';

interface PeriodOption {
  value: string; // 'YYYY-MM' or the 'last-2-weeks' sentinel
  label: string; // 'June 2026' or 'Last 2 weeks (Jun 28 – Jul 11, 2026)'
}

@Component({
  selector: 'app-service-desk-sla',
  templateUrl: './service-desk-sla.component.html',
  styleUrls: ['./service-desk-sla.component.scss'],
  standalone: false,
})
export class ServiceDeskSlaComponent implements OnInit, OnDestroy {
  periods: PeriodOption[] = [];
  selectedPeriod = '';

  data: MonthData | null = null;
  isFetching = false;
  errorFetching = false;
  isExporting = false;

  overallPct: number | null = null;
  overallBand: SlaBand | null = null;

  readonly wipNote =
    'Met SLA – WIP and Missed SLA – WIP are open tickets calculated as if they were closed yesterday at 7 PM.';

  // Routes every fetch through switchMap so a period change cancels any still-in-flight
  // request for the previous period — otherwise a slow response can land after a newer
  // one and overwrite the dashboard with stale data.
  private readonly periodRequested$ = new Subject<string>();
  private readonly destroyed$ = new Subject<void>();

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    this.periods = this.buildPeriodOptions();
    this.selectedPeriod = LAST_TWO_WEEKS;

    this.periodRequested$
      .pipe(
        switchMap((period) => {
          this.isFetching = true;
          this.errorFetching = false;
          const params = period === LAST_TWO_WEEKS ? { period } : { month: period };
          return this.http.get<MonthData>(`${environment.restUrl}/v1/msp/service-desk-sla`, { params }).pipe(
            catchError(() => {
              this.errorFetching = true;
              this.data = null;
              return EMPTY;
            }),
            finalize(() => (this.isFetching = false)),
          );
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe((data) => {
        this.data = data;
        this.overallPct = pctMet(data.summary);
        this.overallBand = slaBand(this.overallPct);

        if (this.selectedPeriod === LAST_TWO_WEEKS) {
          this.correctLastTwoWeeksLabel(data.monthEndDate);
        }
      });

    this.fetch();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  onPeriodChange(period: string): void {
    this.selectedPeriod = period;
    this.fetch();
  }

  retry(): void {
    this.fetch();
  }

  /** Export the currently-loaded dashboard to a styled Excel workbook. ExcelJS (~840 KB) is
   *  loaded on demand so it stays out of the app bundle until the user actually exports. */
  async exportExcel(): Promise<void> {
    if (!this.data || this.isExporting) return;
    this.isExporting = true;
    const label = this.periods.find((p) => p.value === this.selectedPeriod)?.label ?? '';
    try {
      const { exportSlaDashboardToExcel } = await import('./sla-excel-export');
      await exportSlaDashboardToExcel(this.data, label);
    } finally {
      this.isExporting = false;
    }
  }

  /** 'Last 2 weeks' (the two complete Sun–Sat weeks before this one) plus the last full month
   *  and the 11 before it. */
  private buildPeriodOptions(): PeriodOption[] {
    const opts: PeriodOption[] = [
      { value: LAST_TWO_WEEKS, label: this.buildLastTwoWeeksLabel(this.estimateLastCompletedFriday()) },
    ];
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    for (let i = 0; i < 12; i++) {
      const d = new Date(start.getFullYear(), start.getMonth() - i, 1);
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleString('en-US', { month: 'long', year: 'numeric' });
      opts.push({ value, label });
    }
    return opts;
  }

  /**
   * Browser-clock estimate of the last completed Friday, used only to render the dropdown
   * label before the first fetch resolves. The server resolves the authoritative window in
   * America/New_York (see `resolveLastTwoWeeks` in the API), which can name a different Friday
   * than this local-clock guess near the Friday/Saturday Eastern-time boundary — the label is
   * corrected via `correctLastTwoWeeksLabel` once real data arrives.
   */
  private estimateLastCompletedFriday(): Date {
    const now = new Date();
    const friday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
    while (friday.getDay() !== 5) {
      friday.setDate(friday.getDate() - 1);
    }
    return friday;
  }

  /** Display label for the two-week window anchored on [lastCompletedFriday], widened to the
   *  full Sun–Sat calendar weeks: the Sunday before the earlier Monday through the Saturday
   *  after the anchor Friday. */
  private buildLastTwoWeeksLabel(lastCompletedFriday: Date): string {
    const earlierSunday = new Date(lastCompletedFriday);
    earlierSunday.setDate(earlierSunday.getDate() - 12);

    const laterSaturday = new Date(lastCompletedFriday);
    laterSaturday.setDate(laterSaturday.getDate() + 1);

    const start = earlierSunday.toLocaleString('en-US', { month: 'short', day: 'numeric' });
    const end = laterSaturday.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `Last 2 weeks (${start} – ${end})`;
  }

  /** Replaces the browser-clock estimate with the label for the server's authoritative window,
   *  once a last-2-weeks response arrives — `monthEndDate` is that window's last completed
   *  Friday, resolved in America/New_York. */
  private correctLastTwoWeeksLabel(monthEndDate: string): void {
    const option = this.periods.find((p) => p.value === LAST_TWO_WEEKS);
    if (!option) return;
    const [year, month, day] = monthEndDate.split('-').map(Number);
    option.label = this.buildLastTwoWeeksLabel(new Date(year, month - 1, day));
  }

  private fetch(): void {
    if (!this.selectedPeriod) return;
    this.periodRequested$.next(this.selectedPeriod);
  }
}
